import { Card, CardContent } from '@/components/ui/card';
import { useState, useEffect, memo } from 'react';

interface ChecklistItem {
  id: string;
  item: string;
  comment: string;
}

interface ChecklistPreviewCardProps {
  title: string;
  items: ChecklistItem[];
  onClick: () => void;
  className?: string;
}

const ChecklistPreviewCardComponent = ({ 
  title, 
  items, 
  onClick,
  className = ''
}: ChecklistPreviewCardProps) => {
  const [previewCount, setPreviewCount] = useState(10);

  useEffect(() => {
    const updatePreviewCount = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setPreviewCount(3); // Mobile
      } else if (width < 1024) {
        setPreviewCount(7); // Tablet
      } else {
        setPreviewCount(10); // Desktop
      }
    };

    updatePreviewCount();
    window.addEventListener('resize', updatePreviewCount);
    return () => window.removeEventListener('resize', updatePreviewCount);
  }, []);

  const previewItems = items.filter(item => item.item && item.item.trim() !== '').slice(0, previewCount);
  const itemCount = items.filter(item => item.item && item.item.trim() !== '').length;

  return (
    <Card 
      className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:border-accent hover:-translate-y-1 active:translate-y-0 border-l-4 border-l-primary ${className}`}
      onClick={onClick}
    >
      <CardContent className="pt-12 px-6 pb-6">
        {/* Title */}
        <h3 className="text-lg font-normal text-primary mb-4 truncate">
          {title}
        </h3>

        {/* Stats Bar */}
        <div className="flex items-center gap-3 mb-4 text-sm text-muted-foreground">
          <span>{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
        </div>

        {/* Preview Items */}
        {previewItems.length > 0 && (
          <div className="relative max-h-32 md:max-h-48 lg:max-h-80 overflow-hidden">
            <div className="space-y-2">
              {previewItems.map((item, index) => (
                  <div 
                    key={item.id}
                    className="text-sm text-muted-foreground truncate"
                    style={{
                      opacity: Math.max(0.2, 1 - (index * 0.05))
                    }}
                  >
                    {item.item}
                  </div>
              ))}
            </div>
            
            {/* Fade gradient overlay */}
            <div 
              className="absolute bottom-0 left-0 right-0 pointer-events-none h-20 md:h-24 lg:h-32"
              style={{
                background: 'linear-gradient(to bottom, transparent, hsl(var(--card)))'
              }}
            />
          </div>
        )}

        {previewItems.length === 0 && (
          <div className="text-sm text-muted-foreground/50 italic">
            No items yet
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const ChecklistPreviewCard = memo(ChecklistPreviewCardComponent);
